/**
 * Tenant-level menu visibility registry.
 *
 * Internal admins can toggle individual sidebar menu items per client via
 * `Client.features.menuOverrides`. Each item carries a stable `MenuKey`; the
 * sidebar resolves visibility through `resolveMenuItemEnabled()`, which
 * returns the explicit override when one is set and otherwise falls back to
 * the per-key default (with broker items defaulting to the master
 * `enhancedBrokerPortal` toggle).
 *
 * To add a new controllable menu item:
 *   1. Add the key to `MENU_FLAGS` here with the right audience and default.
 *   2. Tag the item in `app-sidebar.tsx` with that `MenuKey`.
 */

import {CLIENT_SLUGS} from './constants/clients';
import {
  findPositionBySystemSubStage,
  resolveAccountsViewConfig,
  type AccountsViewConfig,
} from './accountsView';
import type {OutboundEmailConfig} from './emailSending';
import type {FtpWritesConfig} from './ftpWrites';

export type MenuAudience = 'broker' | 'client' | 'group';

export const MENU_KEYS = [
  // Broker portal
  'broker.dashboard',
  'broker.groups',
  'broker.team',
  'broker.pipeline',
  'broker.messages',
  'broker.documents',
  'broker.resources',
  'broker.contracts',
  'broker.training',
  'broker.pitchTraining',
  'broker.profile',
  'broker.support',
  'broker.accounts',
  'broker.tickets',

  // Client admin portal — top-level
  'client.allGroups',
  'client.accounts',
  'client.memberSearch',
  'client.proposalGenerator',
  'client.beep',
  'client.utilizationReport',
  'client.tickets',
  'client.integrations.adp',

  // Client admin portal — Brokers section
  'client.brokers.tree',
  'client.brokers.messages',
  'client.brokers.training',
  'client.brokers.trainingPitches',
  'client.brokers.pipeline',
  'client.brokers.documents',
  'client.brokers.resources',
  'client.brokers.contracts',
  'client.brokers.support',

  // Client admin portal — Broker profile detail tabs (/brokers/:id)
  'client.brokerProfile.overview',
  'client.brokerProfile.banking',
  'client.brokerProfile.documents',
  'client.brokerProfile.contracts',
  'client.brokerProfile.messages',
  'client.brokerProfile.support',
  'client.brokerProfile.training',
  'client.brokerProfile.network',
  'client.brokerProfile.notes',
  'client.brokerProfile.notifications',

  // Client admin portal — Administration section
  'client.administration.users',

  // Group portal
  'group.myGroup',
  'group.allMembers',
  'group.myGroups',
  'group.memberSearch',
  'group.openEnrollment',
  'group.resources',
  'group.messages',
  'group.integrations.adp',
] as const;

export type MenuKey = (typeof MENU_KEYS)[number];

/**
 * Keys of `ClientFeaturesShape` whose value is a plain boolean toggle (i.e.
 * everything except the `menuOverrides` map). A menu flag can defer its default
 * visibility to one of these so that visibility tracks a feature flag without
 * the sidebar having to special-case it.
 */
type BooleanFeatureKey = {
  [K in keyof ClientFeaturesShape]-?: NonNullable<ClientFeaturesShape[K]> extends boolean
    ? K
    : never;
}[keyof ClientFeaturesShape];

/**
 * Default visibility when no explicit `menuOverrides` entry is set:
 *  - `boolean` — unconditional default, independent of any feature flag.
 *  - `'master'` — sugar for `{feature: 'enhancedBrokerPortal'}` (the broker
 *    master toggle).
 *  - `{feature}` — defer to the named boolean flag on `Client.features`
 *    (true → visible). Used for items whose availability is feature-gated, e.g.
 *    `ticketing`, which also requires a paired `PortalTicketConfig` row.
 *    `whenUnset` is the visibility while the deferred-to flag is absent from
 *    `Client.features` — set it to `true` for opt-out flags (defaults to
 *    `false`, i.e. opt-in).
 */
type DefaultVisibility = boolean | 'master' | {feature: BooleanFeatureKey; whenUnset?: boolean};

export type MenuFlagDefinition = {
  key: MenuKey;
  audience: MenuAudience;
  label: string;
  description?: string;
  /** Visibility when no explicit override is set. */
  defaultVisible: DefaultVisibility;
  /**
   * Case-insensitive client-name substrings (typically tenant slugs from
   * `CLIENT_SLUGS`) that gate where this flag's *toggle* is surfaced in the
   * internal feature-flag console. When set, the toggle only appears for
   * matching tenants; for every other tenant the flag stays at its
   * `defaultVisible` value with no editable control. Leave unset to show the
   * toggle for all tenants. Does not affect `resolveMenuItemEnabled` — runtime
   * visibility still flows through `menuOverrides` + `defaultVisible`.
   */
  restrictToClientKeywords?: readonly string[];
};

export const MENU_FLAGS: readonly MenuFlagDefinition[] = [
  // ----- Broker portal -----
  {
    key: 'broker.dashboard',
    audience: 'broker',
    label: 'Dashboard',
    defaultVisible: 'master',
  },
  {
    key: 'broker.groups',
    audience: 'broker',
    label: 'My Groups',
    description:
      'Always visible to brokers when no override is set, even with the master toggle off.',
    // Special-case: brokers always see "My Groups" as the fallback nav. Only
    // an explicit `false` override hides it.
    defaultVisible: true,
  },
  {
    key: 'broker.team',
    audience: 'broker',
    label: 'My Team',
    description:
      'Read-only downline view (pipeline + live groups per broker). Only rendered for brokers who actually have a downline.',
    defaultVisible: 'master',
  },
  {key: 'broker.pipeline', audience: 'broker', label: 'Pipeline', defaultVisible: 'master'},
  {key: 'broker.messages', audience: 'broker', label: 'Announcements', defaultVisible: 'master'},
  {key: 'broker.documents', audience: 'broker', label: 'Documents', defaultVisible: 'master'},
  {key: 'broker.resources', audience: 'broker', label: 'Resources', defaultVisible: 'master'},
  {key: 'broker.contracts', audience: 'broker', label: 'Contracts', defaultVisible: 'master'},
  {key: 'broker.training', audience: 'broker', label: 'Training', defaultVisible: 'master'},
  {
    key: 'broker.pitchTraining',
    audience: 'broker',
    label: 'Pitch Training',
    defaultVisible: 'master',
  },
  {key: 'broker.profile', audience: 'broker', label: 'My Profile', defaultVisible: 'master'},
  {
    key: 'broker.support',
    audience: 'broker',
    label: 'Help & Support',
    defaultVisible: 'master',
  },
  {
    key: 'broker.accounts',
    audience: 'broker',
    label: 'Accounts',
    description:
      'Unified Accounts pipeline page for brokers. Off by default; enable per tenant to roll out alongside the legacy Pipeline view.',
    defaultVisible: false,
  },
  {
    key: 'broker.tickets',
    audience: 'broker',
    label: 'Tickets',
    description:
      'Broker↔admin ticketing. Visibility defers to the `ticketing` feature flag, which requires a paired PortalTicketConfig row.',
    defaultVisible: {feature: 'ticketing'},
  },

  // ----- Client admin portal -----
  {key: 'client.allGroups', audience: 'client', label: 'All Groups', defaultVisible: true},
  {
    key: 'client.accounts',
    audience: 'client',
    label: 'Accounts',
    description:
      'Unified Accounts pipeline page. Off by default; enable per tenant to roll out alongside the legacy Deals/Prospects views.',
    defaultVisible: false,
  },
  {
    key: 'client.memberSearch',
    audience: 'client',
    label: 'Member Search',
    defaultVisible: true,
  },
  {
    key: 'client.proposalGenerator',
    audience: 'client',
    label: 'Proposal Generator',
    description:
      'Opt-in per client app (Quantum, HealthCues, Synrgy). Defaults visible where the app wires it up; turn the override off to hide it for this tenant.',
    defaultVisible: true,
  },
  {
    key: 'client.beep',
    audience: 'client',
    label: 'BEEP',
    description:
      'Synrgy-only BEEP engagement tab. Off by default and only wired into the Synrgy app; turn the override on to surface the tab (the embedded iframe is still in progress).',
    defaultVisible: false,
    restrictToClientKeywords: [CLIENT_SLUGS.SYNRGY],
  },
  {
    key: 'client.utilizationReport',
    audience: 'client',
    label: 'Utilization Report',
    description:
      'Embedded utilization report. Off by default. The toggle is limited to the portals the report service is onboarded for (HealthCues, Quantum); forcing it visible is what lets that tenant mint report tokens.',
    defaultVisible: false,
    restrictToClientKeywords: [CLIENT_SLUGS.HEALTHCUES, CLIENT_SLUGS.QUANTUM],
  },
  {
    key: 'client.integrations.adp',
    audience: 'client',
    label: 'ADP Integration',
    description:
      "Integrations → ADP for client admins: each group's ADP connection and link code. Visible by default where the app wires it up (HealthCues). Turning the override off hides the menu and pages only; the ADP API still answers client admins. Employers have their own flag, group.integrations.adp, which gates the API too.",
    defaultVisible: true,
    restrictToClientKeywords: [CLIENT_SLUGS.HEALTHCUES],
  },
  {
    key: 'client.tickets',
    audience: 'client',
    label: 'Tickets',
    description:
      'Broker↔admin ticketing. Visibility defers to the `ticketing` feature flag, which requires a paired PortalTicketConfig row.',
    defaultVisible: {feature: 'ticketing'},
  },
  {
    key: 'client.brokers.tree',
    audience: 'client',
    label: 'Brokers · Broker Tree',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.messages',
    audience: 'client',
    label: 'Brokers · Announcements',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.training',
    audience: 'client',
    label: 'Brokers · Training',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.trainingPitches',
    audience: 'client',
    label: 'Brokers · Training Pitches',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.pipeline',
    audience: 'client',
    label: 'Brokers · Pipeline',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.documents',
    audience: 'client',
    label: 'Brokers · Documents',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.resources',
    audience: 'client',
    label: 'Brokers · Resources',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.contracts',
    audience: 'client',
    label: 'Brokers · Contracts',
    defaultVisible: true,
  },
  {
    key: 'client.brokers.support',
    audience: 'client',
    label: 'Brokers · Support',
    defaultVisible: true,
  },

  // Tabs on the broker profile detail page (/brokers/:id). All default visible;
  // turn an override off to hide that tab for this tenant. If every tab is
  // hidden the page falls back to an empty state.
  {
    key: 'client.brokerProfile.overview',
    audience: 'client',
    label: 'Broker Profile · Overview',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.banking',
    audience: 'client',
    label: 'Broker Profile · Banking',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.documents',
    audience: 'client',
    label: 'Broker Profile · Documents',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.contracts',
    audience: 'client',
    label: 'Broker Profile · Contracts',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.messages',
    audience: 'client',
    label: 'Broker Profile · Announcements',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.support',
    audience: 'client',
    label: 'Broker Profile · Support',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.training',
    audience: 'client',
    label: 'Broker Profile · Training',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.network',
    audience: 'client',
    label: 'Broker Profile · Network',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.notes',
    audience: 'client',
    label: 'Broker Profile · Notes',
    defaultVisible: true,
  },
  {
    key: 'client.brokerProfile.notifications',
    audience: 'client',
    label: 'Broker Profile · Notifications',
    defaultVisible: true,
  },

  {
    key: 'client.administration.users',
    audience: 'client',
    label: 'Administration · Users',
    defaultVisible: true,
  },

  // ----- Group portal -----
  {
    key: 'group.myGroup',
    audience: 'group',
    label: 'My Group (single-group user)',
    defaultVisible: true,
  },
  {
    key: 'group.allMembers',
    audience: 'group',
    label: 'All Members (single-group user)',
    defaultVisible: true,
  },
  {
    key: 'group.myGroups',
    audience: 'group',
    label: 'My Groups (multi-group user)',
    defaultVisible: true,
  },
  {key: 'group.memberSearch', audience: 'group', label: 'Member Search', defaultVisible: true},
  {
    key: 'group.openEnrollment',
    audience: 'group',
    label: 'Open Enrollment',
    description:
      'Open Enrollment dashboard for group HR admins (registration progress, not-yet-registered roster, CSV export). Visibility defers to the `openEnrollmentDashboard` feature flag, which is on by default — it is also the default landing page for single-group users.',
    defaultVisible: {feature: 'openEnrollmentDashboard', whenUnset: true},
  },
  {
    key: 'group.resources',
    audience: 'group',
    label: 'Resources',
    description:
      'Resource library for group users — a separate library from broker Resources, managed on the client-admin Group Resources page (Groups → Resources), which this flag also gates. Off by default; enable per tenant to roll out.',
    defaultVisible: false,
  },
  {
    key: 'group.messages',
    audience: 'group',
    label: 'Messages',
    description:
      'Read-only message inbox for group users (broadcasts from client admins and automated system messages). Also gates the client-admin Group Messages page (Groups → Messages). Off by default; enable per tenant to roll out.',
    defaultVisible: false,
  },
  {
    key: 'group.integrations.adp',
    audience: 'group',
    label: 'ADP Integration',
    description:
      "Integrations → ADP for employers (group users): their group's ADP connection, workers and pay statements. Also gates the ADP API for them. Off by default; turn it on when ADP launches for this tenant.",
    defaultVisible: false,
    restrictToClientKeywords: [CLIENT_SLUGS.HEALTHCUES],
  },
];

const MENU_FLAGS_BY_KEY: Record<MenuKey, MenuFlagDefinition> = Object.fromEntries(
  MENU_FLAGS.map((f) => [f.key, f]),
) as Record<MenuKey, MenuFlagDefinition>;

export type ClientMenuOverrides = Partial<Record<MenuKey, boolean>>;

export type ClientFeaturesShape = {
  enhancedBrokerPortal?: boolean;
  menuOverrides?: ClientMenuOverrides;
  /** Tag-driven resource folders in the broker + client-admin resource libraries. Defaults to enabled. */
  resourceFolders?: boolean;
  /**
   * Simplified resource card & folder layout for the broker + client-admin
   * resource libraries — the folder-card grid with drill-in + file cards, and
   * tag-named folder titles. Opt-in per tenant; defaults to disabled so tenants
   * keep the standard screens until explicitly enabled.
   */
  simplifiedResourceLayout?: boolean;
  /** Broker↔admin ticketing system. Requires a paired PortalTicketConfig row when enabled. Defaults to disabled. */
  ticketing?: boolean;
  /** "Client Portal Access" step in the RFC wizard. Defaults to enabled; turn off to drop the step for this tenant. */
  rfcClientPortalAccess?: boolean;
  /**
   * "ACH Authorization" step in the RFC wizard (plus every ACH/banking mention
   * in the password-gated flow). Defaults to enabled for every tenant except
   * Synrgy, which defaults off. An explicit boolean always wins, so the step can
   * be turned back on for Synrgy — or off for any other tenant.
   */
  rfcAchAuthorization?: boolean;
  /** "Link existing VBA group" action on the Account → Group Info tab. Opt-in per tenant; defaults to disabled. */
  linkVbaGroup?: boolean;
  /** VIP (CleverBenefits-only) member management — Add VIP button, VIP search rows, and the add-VIP page. Opt-in per tenant; defaults to disabled. */
  vip?: boolean;
  /**
   * External third-party administrators. On for a tenant whose book of business is
   * partly run by organizations other than CBS, each sending its own eligibility
   * file into the EDI store. Controls whether those members and groups appear at
   * all — in member search, group search, the CSV export and the read-only detail
   * pages — plus the "Administered by" column and filter that only make sense once
   * more than one administrator is in play.
   */
  externalTpas?: boolean;
  /** "Deactivate user" action on the member App User card (CleverBenefits app accounts, including Synrgy). Opt-in per tenant; defaults to disabled. */
  appUserDeactivation?: boolean;
  /** Commission Amount box on broker pages. Opt-in per tenant; defaults to disabled. */
  brokerCommissionAmount?: boolean;
  /** Redesigned broker portal UI (cream surface, dark sidebar, restyled broker + broker-management pages). Opt-in per tenant; defaults to disabled so tenants keep the legacy look until enabled. */
  newBrokerUi?: boolean;
  /** Custom Accounts pipeline (stage/sub-stage names, ordering, column count, table columns). Undefined = default pipeline. */
  accountsView?: AccountsViewConfig;
  /**
   * Whether brokers can self-manage (upload, replace, delete) their own personal
   * documents — W-2, I-9, licenses, etc. Defaults to enabled; turn off per tenant
   * to make the broker Documents page read-only (view/download only), leaving
   * uploads to client admins acting on the broker's behalf.
   */
  brokerDocumentSelfService?: boolean;
  /** Bulk broker CSV import wizard under /brokers/import. Defaults to disabled. */
  bulkBrokerImportEnabled?: boolean;
  /** Lets client admins preview the broker portal "as" one of their brokers (read-only impersonation). Defaults to disabled. */
  viewAsBroker?: boolean;
  /** Open Enrollment dashboard for group users (registration progress + not-registered roster). Defaults to enabled — it's the default landing page for single-group users; turn off per tenant to fall back to the group detail page. */
  openEnrollmentDashboard?: boolean;
  /**
   * The "Welcome Call" implementation step (right after RFC Received). Defaults
   * to enabled for Synrgy and Quantum only. An explicit boolean always wins, so
   * the step can be turned off for them — or on for any other tenant.
   */
  welcomeCall?: boolean;
  /**
   * The Synrgy-specific "Clear Spring form" slot in the Documents-tab Intake
   * Package checklist. An explicit boolean always wins; otherwise defaults on
   * for Synrgy (by client name) and off for every other tenant.
   */
  clearSpringForm?: boolean;
};

/**
 * Resolve whether a menu item is visible for a client given its `features`.
 * Explicit overrides win; otherwise the registered default applies — which may
 * be unconditional, defer to the `enhancedBrokerPortal` master toggle, or defer
 * to another boolean feature flag (e.g. `ticketing`). Keys missing from the
 * registry resolve to hidden (default-deny), so a nav entry only becomes
 * visible once its flag is registered in `MENU_FLAGS`.
 */
export function resolveMenuItemEnabled(
  key: MenuKey,
  features: ClientFeaturesShape | null | undefined,
): boolean {
  const override = features?.menuOverrides?.[key];
  if (typeof override === 'boolean') return override;

  const def = MENU_FLAGS_BY_KEY[key];
  if (!def) return false;

  const {defaultVisible} = def;
  if (defaultVisible === 'master') {
    return !!features?.enhancedBrokerPortal;
  }
  if (typeof defaultVisible === 'object') {
    return features?.[defaultVisible.feature] ?? defaultVisible.whenUnset ?? false;
  }
  return defaultVisible;
}

/**
 * Tag-driven resource folders are on by default; the tenant flag only exists to
 * turn them *off* (falling back to a flat resource list). Treat a missing flag
 * the same as `true` so existing tenants keep folders without any backfill.
 */
export function resolveResourceFoldersEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.resourceFolders !== false;
}

/**
 * The simplified resource card & folder layout (folder-card grid + drill-in,
 * tag-named folder titles) is opt-in. Treat a missing flag as `false` so
 * existing tenants keep the standard resource screens until an internal admin
 * enables it per tenant.
 */
export function resolveSimplifiedResourceLayoutEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.simplifiedResourceLayout === true;
}

/**
 * The RFC wizard's "Client Portal Access" step is on by default; the tenant flag
 * only exists to turn it *off* (dropping the step + its review section). Treat a
 * missing flag the same as `true` so existing tenants keep the step without any
 * backfill.
 */
export function resolveRfcClientPortalAccessEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.rfcClientPortalAccess !== false;
}

/**
 * The RFC wizard's "ACH Authorization" step (and every ACH/banking mention in
 * the password-gated flow) is on by default for every tenant except Synrgy,
 * which defaults off. An explicit `rfcAchAuthorization` boolean always wins, so
 * the step can be turned back on for Synrgy — or off for any other tenant — from
 * the internal feature-flag console. The Synrgy default is keyed off the client
 * name (case-insensitive substring), mirroring `restrictToClientKeywords`.
 */
export function resolveRfcAchAuthorizationEnabled(
  features: ClientFeaturesShape | null | undefined,
  clientName: string | null | undefined,
): boolean {
  if (typeof features?.rfcAchAuthorization === 'boolean') {
    return features.rfcAchAuthorization;
  }
  return !(clientName ?? '').toLowerCase().includes(CLIENT_SLUGS.SYNRGY);
}

/**
 * The "Link existing VBA group" action (Account → Group Info tab) is opt-in per
 * tenant: it only appears once an internal admin enables the flag for the
 * client. Treat a missing flag as `false` so it stays hidden by default.
 */
export function resolveLinkVbaGroupEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.linkVbaGroup === true;
}

/**
 * Broker document self-service (upload/replace/delete of their own personal
 * documents) is on by default; the tenant flag only exists to turn it *off*,
 * making the broker Documents page read-only. Treat a missing flag the same as
 * `true` so existing tenants keep broker self-service without any backfill.
 */
export function resolveBrokerDocumentSelfServiceEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.brokerDocumentSelfService !== false;
}

/**
 * VIP member management is opt-in per tenant: the "Add VIP Member" button, the
 * VIP rows in member search, and the add-VIP page only become available once an
 * internal admin enables the flag for the client. Treat a missing flag as
 * `false` so VIP features stay hidden by default.
 */
export function resolveVipEnabled(features: ClientFeaturesShape | null | undefined): boolean {
  return features?.vip === true;
}

export function resolveAppUserDeactivationEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.appUserDeactivation === true;
}

/**
 * External-TPA surfaces are opt-in per tenant. A tenant whose members are all
 * administered by CBS has nothing to show and no reason to carry an "Administered
 * by" column, so treat a missing flag as `false` and stay hidden by default.
 */
export function resolveExternalTpasEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.externalTpas === true;
}

/**
 * The SWOT Call prospect sub-stage is config-driven: a tenant has it enabled
 * whenever their Accounts-view pipeline includes a *visible* sub-stage mapped to
 * the canonical `SWOT_CALL`. Adding a "SWOT Call" stage in the accounts-view
 * editor lights up both the kanban column and the inline SWOT controls
 * (request / skip / schedule / complete / cancel) in one action — there's no
 * separate toggle to keep in sync. The default pipeline has no `SWOT_CALL`
 * sub-stage, so every unconfigured tenant stays unaffected. A SWOT stage that's
 * been hidden disables the feature (the canonical-only fallback match returned
 * by `findPositionBySystemSubStage` is treated as "not enabled").
 */
export function resolveSwotCallEnabled(features: ClientFeaturesShape | null | undefined): boolean {
  const config = resolveAccountsViewConfig(features?.accountsView);
  const position = findPositionBySystemSubStage(config, 'SWOT_CALL');
  return position !== null && !position.stage.hidden;
}

/**
 * The "Welcome Call" implementation step (right after RFC Received) renders for
 * Synrgy and Quantum only — verified against the cbs-portal demo in the
 * 2026-07-08 reconciliation — and stays hidden for every other tenant
 * (HealthCues, Champ, …). An explicit `welcomeCall` boolean always wins, so the
 * step can be toggled either way per tenant; otherwise the default is derived
 * from the client name (same pattern as `resolveRfcAchAuthorizationEnabled`).
 */
export function resolveWelcomeCallEnabled(
  features: ClientFeaturesShape | null | undefined,
  clientName: string | null | undefined,
): boolean {
  if (typeof features?.welcomeCall === 'boolean') {
    return features.welcomeCall;
  }
  const name = (clientName ?? '').toLowerCase();
  return name.includes(CLIENT_SLUGS.SYNRGY) || name.includes(CLIENT_SLUGS.QUANTUM);
}

/**
 * The Clear Spring form is a Synrgy-specific intake document. Mirrors
 * `resolveWelcomeCallEnabled`: an explicit `clearSpringForm` boolean wins,
 * otherwise the default derives from the client name.
 */
export function resolveClearSpringFormEnabled(
  features: ClientFeaturesShape | null | undefined,
  clientName: string | null | undefined,
): boolean {
  if (typeof features?.clearSpringForm === 'boolean') {
    return features.clearSpringForm;
  }
  return (clientName ?? '').toLowerCase().includes(CLIENT_SLUGS.SYNRGY);
}

/**
 * Broker approval in the onboarding flow is config-driven, mirroring
 * `resolveSwotCallEnabled`: a tenant requires the broker review/approval gate
 * whenever their Accounts-view pipeline includes a *visible* sub-stage mapped to
 * the canonical `BROKER_APPROVED`. Unlike SWOT (an opt-in sub-stage absent from
 * the default pipeline), the broker approval sub-stages ARE in the default
 * pipeline, so every unconfigured tenant — and any custom config that keeps the
 * broker step — resolves to `true` and behaves exactly as before. Removing the
 * broker review/approval sub-stages from a tenant's accounts-view config (e.g.
 * Synrgy, who only needs the client admin's approval) turns the gate off: the
 * RFC submit flow routes straight to client review even when a broker is
 * assigned. A broker step that's been hidden disables the gate too (the
 * canonical-only fallback match from `findPositionBySystemSubStage` is treated
 * as "not enabled"), keeping parity with the kanban it no longer renders.
 */
export function resolveBrokerApprovalEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  const config = resolveAccountsViewConfig(features?.accountsView);
  const position = findPositionBySystemSubStage(config, 'BROKER_APPROVED');
  return position !== null && !position.stage.hidden;
}

/**
 * The closing "Submitted for implementation" onboarding sub-stage is config-
 * driven, mirroring `resolveBrokerApprovalEnabled`: a tenant gets the two-step
 * CBS handoff whenever their Accounts-view pipeline includes a *visible*
 * sub-stage mapped to the canonical `SUBMITTED_FOR_IMPLEMENTATION`. When enabled,
 * the admin "Submit to CBS" action lands the account on this sub-stage (still in
 * ONBOARDING) and a follow-on `beginImplementation` action advances it into
 * IMPLEMENTATION/`RFC_RECEIVED`. Absent from the default pipeline, so every
 * unconfigured tenant keeps the single-step submit (MSA Signed → RFC Received).
 */
export function resolveSubmittedForImplementationEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  const config = resolveAccountsViewConfig(features?.accountsView);
  const position = findPositionBySystemSubStage(config, 'SUBMITTED_FOR_IMPLEMENTATION');
  return position !== null && !position.stage.hidden;
}

/**
 * "View as broker" is a sensitive read-only impersonation feature, so it is
 * off by default — only an explicit `true` enables it for a tenant.
 */
export function resolveViewAsBrokerEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.viewAsBroker === true;
}

export function resolveBrokerCommissionAmountEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.brokerCommissionAmount === true;
}

/**
 * The redesigned broker portal UI is opt-in per tenant: it only renders once an
 * internal admin enables the flag for the client. Treat a missing flag as
 * `false` so tenants keep the legacy look by default.
 */
export function resolveNewBrokerUiEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.newBrokerUi === true;
}

/**
 * The Open Enrollment dashboard (group portal) is on by default — it's the
 * default landing page for single-group users. The tenant flag only exists to
 * turn it *off* (falling back to the group detail page), so treat a missing
 * flag the same as `true`.
 */
export function resolveOpenEnrollmentDashboardEnabled(
  features: ClientFeaturesShape | null | undefined,
): boolean {
  return features?.openEnrollmentDashboard !== false;
}

export function getMenuFlag(key: MenuKey): MenuFlagDefinition {
  return MENU_FLAGS_BY_KEY[key];
}

export function getMenuFlagsByAudience(audience: MenuAudience): readonly MenuFlagDefinition[] {
  return MENU_FLAGS.filter((f) => f.audience === audience);
}

/**
 * Whether a menu flag's toggle should be surfaced in the internal feature-flag
 * console for a given tenant. Flags without `restrictToClientKeywords` are
 * shown for everyone; restricted flags only appear for tenants whose name
 * contains one of the keywords (case-insensitive substring match, mirroring the
 * tenant gate in the clients list).
 */
export function isMenuFlagVisibleForClient(
  flag: MenuFlagDefinition,
  clientName: string | null | undefined,
): boolean {
  if (!flag.restrictToClientKeywords || flag.restrictToClientKeywords.length === 0) {
    return true;
  }
  const lowered = (clientName ?? '').toLowerCase();
  return flag.restrictToClientKeywords.some((keyword) => lowered.includes(keyword.toLowerCase()));
}

/* -------------------------------------------------------------------------- */
/*  Internal portal menu flags                                                */
/* -------------------------------------------------------------------------- */

/**
 * Internal-portal menu visibility registry.
 *
 * Unlike the client/broker/group flags above — which are tenant-scoped and
 * live on `Client.features` — internal CBS staff don't belong to a tenant, so
 * these are stored once globally on the singleton `InternalConfig.features`
 * row. There is no master toggle; every item defaults visible and an explicit
 * override hides (or force-shows) it for the whole internal portal.
 *
 * To add a new controllable internal menu item:
 *   1. Add the key to `INTERNAL_MENU_KEYS` here with the right section + default.
 *   2. Tag the item in the internal `app-sidebar.tsx` with that `InternalMenuKey`.
 */
export const INTERNAL_MENU_KEYS = [
  // Top-level
  'internal.groups',
  'internal.memberSearch',
  'internal.reports',
  'internal.adminSettings',
  'internal.jobs',
  'internal.invoices',
  'internal.clients',
  'internal.tickets',
  'internal.permissions',
  'internal.vbaSync',
  'internal.claims',
  'internal.accounts',
  'internal.cbsQueue',

  // Billing
  'internal.billing.carrierReconciliation',

  // Group Plans

  // Users
  'internal.users.all',
  'internal.users.create',

  // Deals
  'internal.deals.create',
  'internal.deals.admin',
  'internal.deals.rfcSubmissions',
  'internal.deals.onboardingPipeline',
  'internal.deals.onboardingTemplates',

  // Audit
  'internal.audit.activity',
] as const;

export type InternalMenuKey = (typeof INTERNAL_MENU_KEYS)[number];

/**
 * Section is a display-only grouping for the admin console; it mirrors the
 * collapsible dropdown groups in the internal sidebar (top-level items use the
 * `'General'` section).
 */
export type InternalMenuSection =
  | 'General'
  | 'Billing'
  | 'Group Plans'
  | 'Users'
  | 'Deals'
  | 'Audit';

export type InternalMenuFlagDefinition = {
  key: InternalMenuKey;
  section: InternalMenuSection;
  label: string;
  description?: string;
  /** Visibility when no explicit override is set. */
  defaultVisible: boolean;
};

export const INTERNAL_MENU_FLAGS: readonly InternalMenuFlagDefinition[] = [
  // ----- General (top-level) -----
  {key: 'internal.groups', section: 'General', label: 'Groups', defaultVisible: true},
  {
    key: 'internal.reports',
    section: 'General',
    label: 'Reports & Analytics',
    description: 'Cross-domain operations KPIs and trend charts.',
    defaultVisible: false,
  },
  {
    key: 'internal.adminSettings',
    section: 'General',
    label: 'Admin Settings',
    description: 'Org-level SLA cadences, alert rules, and carrier catalog.',
    defaultVisible: false,
  },
  {
    key: 'internal.memberSearch',
    section: 'General',
    label: 'Member Search',
    defaultVisible: true,
  },
  {key: 'internal.jobs', section: 'General', label: 'Jobs', defaultVisible: true},
  {key: 'internal.invoices', section: 'General', label: 'Invoices', defaultVisible: true},
  {
    key: 'internal.clients',
    section: 'General',
    label: 'Feature Flags · Client Flags',
    description: 'The per-tenant feature-flag console (the "Client Flags" sub-item).',
    defaultVisible: true,
  },
  {
    key: 'internal.tickets',
    section: 'General',
    label: 'Development Requests',
    description: 'Clever Benefits ticket queue — review tickets forwarded from client admins.',
    defaultVisible: true,
  },
  {
    key: 'internal.permissions',
    section: 'General',
    label: 'Roles and Permissions',
    description:
      'Also gated by the `permissions.manage` permission — hiding it here removes it for users who have that permission too.',
    defaultVisible: true,
  },
  {key: 'internal.vbaSync', section: 'General', label: 'VBA Sync Runs', defaultVisible: true},
  {
    key: 'internal.claims',
    section: 'General',
    label: 'Claims Tracking',
    description:
      'Also gated by the `claims_tracking.view` permission — hiding it here removes it for users who have that permission too.',
    defaultVisible: true,
  },
  {
    key: 'internal.accounts',
    section: 'General',
    label: 'Accounts',
    description:
      'Unified Accounts pipeline page. Off by default; enable to roll out alongside the legacy Deals views.',
    defaultVisible: false,
  },
  {
    key: 'internal.cbsQueue',
    section: 'General',
    label: 'CBS Queue',
    description:
      'CBS RFC intake queue. Hiding it only removes the sidebar entry — /rfc/cbs-queue stays reachable by URL for users with the `rfc.cbs_queue` permission.',
    defaultVisible: false,
  },

  // ----- Billing -----
  {
    key: 'internal.billing.carrierReconciliation',
    section: 'Billing',
    label: 'Carrier Reconciliation',
    description:
      'Also gated by the INTERNAL_ADMIN or INTERNAL_ACCOUNTING role — hiding it here removes it for those users too.',
    defaultVisible: false,
  },

  // ----- Users -----
  {key: 'internal.users.all', section: 'Users', label: 'All Users', defaultVisible: true},
  {key: 'internal.users.create', section: 'Users', label: 'Create User', defaultVisible: true},

  // ----- Deals -----
  {key: 'internal.deals.create', section: 'Deals', label: 'Create New Deal', defaultVisible: true},
  {
    key: 'internal.deals.admin',
    section: 'Deals',
    label: 'Deals Administration',
    defaultVisible: true,
  },
  {
    key: 'internal.deals.rfcSubmissions',
    section: 'Deals',
    label: 'RFC Submissions',
    defaultVisible: true,
  },
  {
    key: 'internal.deals.onboardingPipeline',
    section: 'Deals',
    label: 'Onboarding Pipeline',
    defaultVisible: true,
  },
  {
    key: 'internal.deals.onboardingTemplates',
    section: 'Deals',
    label: 'Onboarding Templates',
    defaultVisible: true,
  },

  // ----- Audit -----
  {
    key: 'internal.audit.activity',
    section: 'Audit',
    label: 'Activity Log',
    description: 'Cross-tenant change/activity history with per-record History tabs.',
    defaultVisible: false,
  },
];

const INTERNAL_MENU_FLAGS_BY_KEY: Record<InternalMenuKey, InternalMenuFlagDefinition> =
  Object.fromEntries(INTERNAL_MENU_FLAGS.map((f) => [f.key, f])) as Record<
    InternalMenuKey,
    InternalMenuFlagDefinition
  >;

export const INTERNAL_MENU_SECTIONS: readonly InternalMenuSection[] = [
  'General',
  'Billing',
  'Group Plans',
  'Users',
  'Deals',
  'Audit',
];

export type InternalMenuOverrides = Partial<Record<InternalMenuKey, boolean>>;

/**
 * Global (org-wide) internal-portal feature flags. Stored on the singleton
 * `InternalConfig.features` row and edited in the internal admin console.
 */
export type InternalFeaturesShape = {
  menuOverrides?: InternalMenuOverrides;
  outboundEmail?: OutboundEmailConfig;
  ftpWrites?: FtpWritesConfig;
  /** Custom Accounts pipeline for the internal portal's accounts view. Undefined = default pipeline. */
  accountsView?: AccountsViewConfig;
  /**
   * The full-featured account detail page (Proposals, RFC, MSA, Payroll, Group
   * Info tabs, sidebar, SWOT controls) on the internal portal. Off by default:
   * internal staff get the basic CBS-style detail view (Overview, Documents,
   * Invoicing, Commissions, Activity) until this is enabled. Client/broker
   * portals are unaffected — they always render the full page.
   */
  advancedAccountDetail?: boolean;
};

/**
 * Resolve whether an internal menu item is visible given the global config.
 * Explicit overrides win; otherwise the item's registered default applies.
 * Keys missing from the registry resolve to hidden (default-deny), so a nav
 * entry only becomes visible once its flag is registered in
 * `INTERNAL_MENU_FLAGS`.
 */
export function resolveInternalMenuItemEnabled(
  key: InternalMenuKey,
  features: InternalFeaturesShape | null | undefined,
): boolean {
  const override = features?.menuOverrides?.[key];
  if (typeof override === 'boolean') return override;

  const def = INTERNAL_MENU_FLAGS_BY_KEY[key];
  if (!def) return false;
  return def.defaultVisible;
}

/**
 * The advanced (full-featured) account detail page is opt-in for the internal
 * portal: only an explicit `true` enables it. Missing/false renders the basic
 * CBS-style detail view.
 */
export function resolveInternalAdvancedAccountDetailEnabled(
  features: InternalFeaturesShape | null | undefined,
): boolean {
  return features?.advancedAccountDetail === true;
}

export function getInternalMenuFlag(key: InternalMenuKey): InternalMenuFlagDefinition {
  return INTERNAL_MENU_FLAGS_BY_KEY[key];
}

export function getInternalMenuFlagsBySection(
  section: InternalMenuSection,
): readonly InternalMenuFlagDefinition[] {
  return INTERNAL_MENU_FLAGS.filter((f) => f.section === section);
}
